import React,
{
  useState,
  useEffect,
  useCallback,
} from "react";

import {
  Building2,
  Filter,
  RefreshCw,
  Target,
  IndianRupee,
  Trophy,
} from "lucide-react";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  PieChart,
  Pie,
} from "recharts";

import { motion } from "framer-motion";

import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import MISFilterDrawer from "../MISFilterDrawer";


const IndustryAnalysisSkeleton = () => {
  return (
    <div className="bg-white rounded-2xl border p-5 animate-pulse">

      <div className="h-6 w-56 bg-gray-200 rounded mb-6" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">

        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="h-24 bg-gray-100 rounded-xl"
          />
        ))}

      </div>

      <div className="h-[450px] bg-gray-100 rounded-xl" />

    </div>
  );
};

const IndustryAnalysisError = ({
  onRetry,
}) => {
  return (
    <div className="bg-white rounded-2xl border border-red-200 p-6 text-center">

      <h3 className="text-red-600 font-semibold">
        Failed to load Industry Analysis
      </h3>

      <button
        onClick={onRetry}
        className="mt-4 px-4 py-2 rounded-lg bg-red-600 text-white"
      >
        Retry
      </button>

    </div>
  );
};

const COLORS = [
  "#DC2626",
  "#F97316",
  "#EAB308",
  "#22C55E",
  "#3B82F6",
  "#8B5CF6",
];

const IndustryAnalysisCard = () => {

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState(false);

  const [data, setData] =
    useState(null);

  const [showFilters, setShowFilters] =
    useState(false);

  const [filters, setFilters] =
    useState({
      filterType: "thisMonth",
      divisionId: "",
      userId: "",
      stageId: "",
      industryType: "",
      fromDate: "",
      toDate: "",
    });

  const [appliedFilters, setAppliedFilters] =
    useState(filters);

  const fetchReport =
    useCallback(async () => {

      try {

        setLoading(true);
        setError(false);

        const params =
          new URLSearchParams();

        Object.entries(
          appliedFilters
        ).forEach(
          ([key, value]) => {

            if (value) {
              params.append(
                key,
                value
              );
            }

          }
        );

        const response =
          await api.get(
            `${SummaryApi.getIndustryAnalysis.url}?${params.toString()}`
          );

        if (
          response?.data?.success
        ) {

          setData(
            response.data.data
          );

        }

      } catch (error) {

        console.error(error);
        setError(true);

      } finally {

        setLoading(false);

      }

    }, [appliedFilters]);

  useEffect(() => {

    fetchReport();

  }, [fetchReport]);

  if (loading) {
    return (
      <IndustryAnalysisSkeleton />
    );
  }

  if (error) {
    return (
      <IndustryAnalysisError
        onRetry={fetchReport}
      />
    );
  }

  const summary =
    data.summary;

  const cards = [
    {
      title:
        "Industries",
      value:
        summary.totalIndustries,
      icon: Building2,
      bg: "bg-blue-50",
      color:
        "text-blue-600",
    },
    {
      title:
        "Total Leads",
      value:
        summary.totalLeads,
      icon: Target,
      bg: "bg-orange-50",
      color:
        "text-orange-600",
    },
    {
      title:
        "Pipeline Value",
      value: `₹${summary.totalPipeline.toLocaleString("en-IN")}`,
      icon: IndianRupee,
      bg: "bg-green-50",
      color:
        "text-green-600",
    },
    {
      title:
        "Top Industry",
      value:
        summary.topIndustry,
      icon: Trophy,
      bg: "bg-red-50",
      color:
        "text-red-600",
    },
  ];

  return (
    <>
      <motion.div
        initial={{
          opacity: 0,
          y: 20,
        }}
        whileInView={{
          opacity: 1,
          y: 0,
        }}
        viewport={{
          once: true,
        }}
        className="bg-white rounded-2xl border shadow-sm"
      >

        <div className="flex items-center justify-between p-5 border-b">

          <div className="flex items-center gap-2">

            <Building2
              size={18}
              className="text-red-600"
            />

            <h2 className="font-semibold text-lg">
              Industry Analysis
            </h2>

          </div>

          <div className="flex gap-2">

            <button
              onClick={fetchReport}
              className="h-9 w-9 rounded-lg border flex items-center justify-center"
            >
              <RefreshCw size={15} />
            </button>

            <button
              onClick={() =>
                setShowFilters(true)
              }
              className="h-9 w-9 rounded-lg bg-red-600 text-white flex items-center justify-center"
            >
              <Filter size={15} />
            </button>

          </div>

        </div>

        <div className="p-5">

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">

            {cards.map((item) => {

              const Icon =
                item.icon;

              return (
                <div
                  key={item.title}
                  className={`${item.bg} rounded-xl p-4`}
                >

                  <div className="flex justify-between">

                    <div>

                      <p className="text-xs text-gray-500">
                        {item.title}
                      </p>

                      <h3 className={`mt-2 text-lg font-bold ${item.color}`}>
                        {item.value}
                      </h3>

                    </div>

                    <Icon
                      size={20}
                      className={item.color}
                    />

                  </div>

                </div>
              );

            })}

          </div>

          <div className="grid xl:grid-cols-2 gap-6">

            <div className="border rounded-xl p-4 h-[420px]">

              <h3 className="font-semibold mb-4">
                Industry Lead Distribution
              </h3>

              <ResponsiveContainer
                width="100%"
                height="100%"
              >

                <BarChart
                  data={data.chart}
                >

                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis
                    dataKey="industry"
                    angle={-15}
                    textAnchor="end"
                    height={80}
                  />

                  <YAxis />

                  <Tooltip />

                  <Bar
                    dataKey="leads"
                    radius={[6,6,0,0]}
                  >

                    {data.chart.map((_, index) => (
                      <Cell
                        key={index}
                        fill={COLORS[index]}
                      />
                    ))}

                  </Bar>

                </BarChart>

              </ResponsiveContainer>

            </div>

            <div className="border rounded-xl p-4 h-[420px]">

              <h3 className="font-semibold mb-4">
                Revenue Contribution
              </h3>

              <ResponsiveContainer
                width="100%"
                height="100%"
              >

                <PieChart>

                  <Pie
                    data={data.chart}
                    dataKey="revenue"
                    nameKey="industry"
                    outerRadius={130}
                    label
                  >

                    {data.chart.map((_, index) => (
                      <Cell
                        key={index}
                        fill={COLORS[index]}
                      />
                    ))}

                  </Pie>

                  <Tooltip />

                </PieChart>

              </ResponsiveContainer>

            </div>

          </div>

          <div className="mt-6 border rounded-xl overflow-hidden">

            <table className="w-full">

              <thead className="bg-gray-50">

                <tr>

                  <th className="px-4 py-3 text-left">
                    Industry
                  </th>

                  <th className="px-4 py-3 text-left">
                    Leads
                  </th>

                  <th className="px-4 py-3 text-left">
                    Active
                  </th>

                  <th className="px-4 py-3 text-left">
                    Lost
                  </th>

                  <th className="px-4 py-3 text-left">
                    Pipeline
                  </th>

                  <th className="px-4 py-3 text-left">
                    Forecast
                  </th>

                </tr>

              </thead>

              <tbody>

                {data.industries.map((row) => (

                  <tr
                    key={row.industry}
                    className="border-t"
                  >

                    <td className="px-4 py-3 font-medium">
                      {row.industry}
                    </td>

                    <td className="px-4 py-3">
                      {row.leadCount}
                    </td>

                    <td className="px-4 py-3 text-blue-600">
                      {row.activeLeads}
                    </td>

                    <td className="px-4 py-3 text-red-600">
                      {row.lostLeads}
                    </td>

                    <td className="px-4 py-3">
                      ₹{row.pipelineValue.toLocaleString("en-IN")}
                    </td>

                    <td className="px-4 py-3 text-green-600">
                      ₹{row.forecastRevenue.toLocaleString("en-IN")}
                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

          </div>

          <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-5">

            <h3 className="font-semibold text-red-700 mb-3">
              Industry Insights
            </h3>

            <ul className="space-y-2">

              {data.insights.map((item, index) => (

                <li
                  key={index}
                  className="text-sm text-gray-700"
                >
                  • {item}
                </li>

              ))}

            </ul>

          </div>

        </div>

      </motion.div>

      <MISFilterDrawer
        open={showFilters}
        onClose={() =>
          setShowFilters(false)
        }
        filters={filters}
        setFilters={setFilters}
        onApply={() => {

          setAppliedFilters(filters);

          setShowFilters(false);

        }}
      />
    </>
  );
};

export default IndustryAnalysisCard;